import {
  collection,
  getDocs,
  getDocsFromServer,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type QuerySnapshot,
  type Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';

import { db } from '@/src/lib/firebase';

export const MISSION_STATEMENT_PARAGRAPHS = [
  'Frienzzi exists to make social connection feel honest, useful, and positive again.',
  'Many social platforms ask people to trust systems they cannot see. They change feeds without clear explanations, reward conflict because it keeps people watching, and make important product decisions behind closed doors.',
  'Frienzzi is choosing a different path.',
  'We are committed to transparency, positivity, and accountability. When we change how Frienzzi works, how content is organized, how notifications are sent, how privacy is handled, or how algorithms affect what people see, we will explain those changes in a public changelog written for people, not engineers.',
  'Our goal is simple: help real friends share real moments without turning attention into outrage.',
  'Frienzzi will grow in public, explain its decisions, and keep building toward a more positive social network.',
] as const;

export type ChangeLogCategory =
  | 'functionality'
  | 'algorithm'
  | 'privacy'
  | 'safety'
  | 'notifications'
  | 'email'
  | 'bugfix'
  | 'other';

export type ChangeLogImpact = 'major' | 'minor';
export type ChangeLogStatus = 'draft' | 'published';
export type ChangeLogVisibility = 'public' | 'internal';

export type ChangeLogEntry = {
  id: string;
  title: string;
  summary: string;
  details: string;
  category: ChangeLogCategory;
  impact: ChangeLogImpact;
  status: ChangeLogStatus;
  visibility: ChangeLogVisibility;
  effectiveAt: Timestamp | null;
  publishedAt: Timestamp | null;
  commitSha: string | null;
  releaseId: string | null;
  deployTarget: string | null;
  authorName: string | null;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
};

function parseString(value: unknown, fallback = '') {
  return typeof value === 'string' ? value : fallback;
}

function parseNullableString(value: unknown) {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function parseCategory(value: unknown): ChangeLogCategory {
  return value === 'functionality' ||
    value === 'algorithm' ||
    value === 'privacy' ||
    value === 'safety' ||
    value === 'notifications' ||
    value === 'email' ||
    value === 'bugfix' ||
    value === 'other'
    ? value
    : 'other';
}

function parseImpact(value: unknown): ChangeLogImpact {
  return value === 'major' ? 'major' : 'minor';
}

function parseChangeLogSnapshot(
  snapshot: QuerySnapshot,
): ChangeLogEntry[] {
  return snapshot.docs.map((entrySnapshot) => {
    const data = entrySnapshot.data();

    return {
      id: entrySnapshot.id,
      title: parseString(data.title, 'Frienzzi update'),
      summary: parseString(data.summary),
      details: parseString(data.details),
      category: parseCategory(data.category),
      impact: parseImpact(data.impact),
      status: data.status === 'published' ? 'published' : 'draft',
      visibility: data.visibility === 'public' ? 'public' : 'internal',
      effectiveAt: (data.effectiveAt as Timestamp | null | undefined) ?? null,
      publishedAt: (data.publishedAt as Timestamp | null | undefined) ?? null,
      commitSha: parseNullableString(data.commitSha),
      releaseId: parseNullableString(data.releaseId),
      deployTarget: parseNullableString(data.deployTarget),
      authorName: parseNullableString(data.authorName),
      createdAt: (data.createdAt as Timestamp | null | undefined) ?? null,
      updatedAt: (data.updatedAt as Timestamp | null | undefined) ?? null,
    };
  });
}

function publishedChangeLogQuery(limitCount: number) {
  return query(
    collection(db, 'changeLogEntries'),
    where('status', '==', 'published'),
    where('visibility', '==', 'public'),
    orderBy('publishedAt', 'desc'),
    limit(limitCount),
  );
}

export function subscribeToPublishedChangeLogEntries({
  limitCount = 20,
  onError,
  onNext,
}: {
  limitCount?: number;
  onError: (error: Error) => void;
  onNext: (entries: ChangeLogEntry[]) => void;
}): Unsubscribe {
  return onSnapshot(
    publishedChangeLogQuery(limitCount),
    (snapshot) => {
      onNext(parseChangeLogSnapshot(snapshot));
    },
    onError,
  );
}

export async function fetchPublishedChangeLogEntries(limitCount = 20) {
  const entryQuery = publishedChangeLogQuery(limitCount);

  try {
    const snapshot = await getDocsFromServer(entryQuery);
    return parseChangeLogSnapshot(snapshot);
  } catch {
    // Fall back to the normal SDK path so an offline/stale PWA can still use
    // any cached public changelog entries instead of failing hard.
  }

  const snapshot = await getDocs(entryQuery);
  return parseChangeLogSnapshot(snapshot);
}
